"use client";

import * as React from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef as TanStackColumnDef,
  type ColumnFiltersState,
  type ColumnSizingState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { cn } from "@aio/platform-ui";
import type { ColumnDef } from "./columns";
import { resolveAccessor } from "./columns";
import type { FormatterBundle } from "./lib/format";
import { useTableState, type TableState } from "./use-table-state";
import { useVirtualRows } from "./use-virtual-rows";
import { useSavedViews } from "./use-saved-views";
import { useKeyboardNav } from "./use-keyboard-nav";
import { TableHeader } from "./header/table-header";
import { ColumnMenu } from "./header/column-menu";
import { ColumnVisibility } from "./header/column-visibility";
import { DataRow } from "./rows/data-row";
import { SelectionAnchorProvider } from "./rows/selection-cell";
import { Pagination } from "./footer/pagination";
import { BulkActionBar } from "./footer/bulk-action-bar";
import { FilterBar } from "./filters/filter-bar";
import type { ActiveFilter } from "./filters/filter-input";
import type { ColumnWithCustomOperators } from "./filters/operators";
import { ExportMenu, type ExportFormat } from "./export/export-menu";
import { SavedViewsMenu } from "./views/saved-views-menu";
import { EmptyTable } from "./states/empty-table";
import { LoadingTable } from "./states/loading-table";
import { ErrorTable } from "./states/error-table";

/**
 * Locale string accepted by the table. Mirrors `SupportedLocale` from
 * `@aio/platform-i18n` but kept as a plain string here so this package does
 * not import that module — see the `formatters` prop for the rationale.
 */
export type SupportedLocale = "sr-Latn" | "sr-Cyrl" | "en";

/**
 * One bulk action available in the bulk action bar. `onRun` receives the
 * currently-selected row ids in display order.
 */
export interface BulkAction {
  /** Stable id; used as the React key and as a `data-testid` suffix. */
  id: string;
  /** User-visible label rendered on the action button. */
  label: string;
  /** Callback invoked with the selected ids when the button is clicked. */
  onRun: (selectedIds: string[]) => void | Promise<void>;
}

/**
 * Props for {@link DataTable}.
 *
 * Either `data` (client-side mode) or `dataSource` (server-side mode) must be
 * provided. Passing both is a programming error — `dataSource` wins, and a
 * dev-only warning fires once.
 */
export interface DataTableProps<TRow> {
  /** Declarative column definitions. */
  columns: Array<ColumnDef<TRow> & ColumnWithCustomOperators>;
  /**
   * Static row array for client-side mode. The table runs sort / filter /
   * pagination locally against this dataset.
   */
  data?: TRow[];
  /**
   * Async loader for server-side mode. Called whenever the relevant slices
   * of `state` change (sorting, filters, pagination). The promise resolves
   * with the slice of rows for the current page plus the unfiltered total
   * (used to derive page count).
   */
  dataSource?: (state: TableState) => Promise<{ rows: TRow[]; total: number }>;
  /**
   * Stable id derivation. Used for selection state, keyboard nav, and any
   * downstream row tracking. Required because TanStack defaults to row index
   * which is unstable across pagination changes.
   */
  rowId: (row: TRow) => string;
  /**
   * When provided, every state change is persisted to localStorage under
   * `aio:table:${persistKey}` and saved views live under
   * `aio:tableviews:${persistKey}`. Omit to keep the table purely in-memory.
   */
  persistKey?: string;
  /**
   * Slot rendered to the right of the toolbar — typically a "+ Create" or
   * import button. Not coupled to any specific intent so the caller can use
   * any button-shaped node.
   */
  primaryAction?: React.ReactNode;
  /**
   * Bulk actions surfaced in the floating bar when at least one row is
   * selected. Each action is invoked with the array of selected row ids.
   */
  bulkActions?: BulkAction[];
  /** Custom empty-state node. Defaults to {@link EmptyTable}. */
  emptyState?: React.ReactNode;
  /**
   * Called when a row is "activated" — clicked anywhere outside the
   * selection cell, or focused and Enter pressed. Typical action is to push
   * to a detail route.
   */
  onRowActivate?: (row: TRow) => void;
  /**
   * Called when the user picks a format from the toolbar's export menu.
   *
   * The composer intentionally does NOT bundle export writers — `toCsv` /
   * `toXlsx` / `toPdf` are exposed from the package barrel so callers can
   * pick which writers to ship (and thus pay the bundle cost for). The
   * default behaviour when this prop is omitted is a `console.warn` that
   * tells the developer to wire a handler. That dev-only signal is the
   * documented escape hatch — production callers always pass `onExport`.
   *
   * The callback receives the format id plus a snapshot of the rows that
   * are currently visible after sort + filter (but BEFORE pagination — most
   * users want the full filtered set in their export, not just one page)
   * and the columns that are currently visible. Callers route this to the
   * appropriate writer (`toCsv` / `toXlsx` / `toPdf`).
   */
  onExport?: (
    format: ExportFormat,
    snapshot: { rows: TRow[]; columns: ColumnDef<TRow>[] },
  ) => void | Promise<void>;
  /**
   * i18n locale used by cell formatters. Pass `useLocale().locale` from the
   * caller — this component does not consume the i18n context directly so it
   * can be reused outside the platform's i18n provider (e.g. in tests).
   */
  locale?: SupportedLocale;
  /**
   * Formatter bundle. The caller is expected to wire in
   * `@aio/platform-i18n`'s formatters in production. When omitted, the
   * component falls back to native Intl with sensible defaults — this is the
   * single documented exception to the project's "no silent fallbacks" rule
   * because the alternative (crash on missing formatters) is hostile to the
   * common case where the caller just wants to see something.
   */
  formatters?: FormatterBundle;
  /** Container className. */
  className?: string;
  /** `data-testid` forwarded to the outer wrapper. Defaults to "data-table". */
  "data-testid"?: string;
}

/**
 * Default formatter bundle backed by native Intl.
 *
 * Surfaced as a module-level constant rather than an inline expression so the
 * "fallback to Intl" path is auditable from a single grep — see the
 * `formatters` prop docstring for the policy rationale.
 */
const defaultFormatters: FormatterBundle = {
  number: (locale, value, options) =>
    new Intl.NumberFormat(locale, options).format(value),
  currency: (locale, value, currency) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "code",
    }).format(value),
  date: (locale, value, options) =>
    new Intl.DateTimeFormat(locale, options ?? { dateStyle: "medium" }).format(
      value,
    ),
};

/**
 * Convert one platform {@link ColumnDef} into the TanStack column shape.
 *
 * The TanStack column reads:
 *   - `id` from `column.key`
 *   - `accessorFn` from `resolveAccessor(column)`
 *   - `header` from `column.header` (caller passes already-resolved string)
 *   - `meta` carrying `align` + `requiredPermission` for downstream styling
 *   - sort/filter/resize capability flags
 *   - `size` / `minSize` / `maxSize` for column sizing
 */
function toTanStackColumn<TRow>(
  column: ColumnDef<TRow>,
): TanStackColumnDef<TRow> {
  const accessor = resolveAccessor(column);
  const def: TanStackColumnDef<TRow> = {
    id: column.key,
    accessorFn: (row) => accessor(row),
    header: column.header,
    enableSorting: column.sortable !== false,
    enableColumnFilter: column.filterable !== false,
    enableHiding: true,
    enableResizing: true,
    enablePinning: true,
    meta: {
      align: column.align,
      requiredPermission: column.requiredPermission,
      type: column.type,
    },
    ...(column.width !== undefined ? { size: column.width } : {}),
    ...(column.minWidth !== undefined ? { minSize: column.minWidth } : {}),
    ...(column.maxWidth !== undefined ? { maxSize: column.maxWidth } : {}),
  };
  return def;
}

/**
 * Convert the engine's {@link ActiveFilter} array into the
 * `ColumnFiltersState` shape TanStack understands. The mapping is one-to-one
 * for value-bearing operators; valueless operators (e.g. `isEmpty`) are
 * encoded as `{ id, value: { __op: operatorId } }` so the filter function can
 * branch on shape.
 */
function activeFiltersToColumnFilters(
  filters: ActiveFilter[],
): ColumnFiltersState {
  return filters.map((f) => ({
    id: f.columnKey,
    value: { operatorId: f.operatorId, value: f.value },
  }));
}

/**
 * Inverse of {@link activeFiltersToColumnFilters}. Used to seed the filter
 * bar from persisted state or saved views.
 */
function columnFiltersToActiveFilters(
  filters: ColumnFiltersState,
): ActiveFilter[] {
  const out: ActiveFilter[] = [];
  for (const f of filters) {
    if (
      f.value &&
      typeof f.value === "object" &&
      "operatorId" in (f.value as Record<string, unknown>)
    ) {
      const v = f.value as { operatorId: string; value: ActiveFilter["value"] };
      out.push({
        columnKey: f.id,
        operatorId: v.operatorId as ActiveFilter["operatorId"],
        value: v.value,
      });
    }
  }
  return out;
}

/**
 * Generic predicate for client-side filter dispatch. Receives the structured
 * `{ operatorId, value }` filter and the cell value, returns true when the
 * row passes. Anything that doesn't match a known operator falls through to
 * `true` so unknown operators don't accidentally hide rows — the upstream
 * UI is the source of truth and a missing predicate is a UI bug, not a data
 * bug.
 */
function evaluateFilter(
  cellValue: unknown,
  filterValue: unknown,
): boolean {
  if (
    !filterValue ||
    typeof filterValue !== "object" ||
    !("operatorId" in (filterValue as Record<string, unknown>))
  ) {
    // Defensive: TanStack passes the raw filter value when the column has no
    // structured filter — treat it as a plain `contains` for strings.
    if (typeof filterValue === "string" && cellValue !== null && cellValue !== undefined) {
      return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
    }
    return true;
  }
  const { operatorId, value } = filterValue as {
    operatorId: string;
    value: ActiveFilter["value"];
  };
  const cv = cellValue;
  const cvStr = cv === null || cv === undefined ? "" : String(cv).toLowerCase();

  switch (operatorId) {
    case "contains":
      return typeof value === "string"
        ? cvStr.includes(value.toLowerCase())
        : true;
    case "equals":
      if (typeof value === "string" && typeof cv === "number") {
        const n = Number(value);
        return Number.isFinite(n) ? cv === n : false;
      }
      return typeof value === "string"
        ? cvStr === value.toLowerCase()
        : cv === value;
    case "startsWith":
      return typeof value === "string"
        ? cvStr.startsWith(value.toLowerCase())
        : true;
    case "endsWith":
      return typeof value === "string"
        ? cvStr.endsWith(value.toLowerCase())
        : true;
    case "gt":
      return typeof value === "string" && typeof cv === "number"
        ? cv > Number(value)
        : true;
    case "gte":
      return typeof value === "string" && typeof cv === "number"
        ? cv >= Number(value)
        : true;
    case "lt":
      return typeof value === "string" && typeof cv === "number"
        ? cv < Number(value)
        : true;
    case "lte":
      return typeof value === "string" && typeof cv === "number"
        ? cv <= Number(value)
        : true;
    case "between": {
      if (Array.isArray(value) && value.length === 2 && typeof cv === "number") {
        const [lo, hi] = value as [string, string];
        const loN = lo ? Number(lo) : Number.NEGATIVE_INFINITY;
        const hiN = hi ? Number(hi) : Number.POSITIVE_INFINITY;
        return cv >= loN && cv <= hiN;
      }
      return true;
    }
    case "in":
      return Array.isArray(value)
        ? (value as string[]).map((v) => v.toLowerCase()).includes(cvStr)
        : true;
    case "notIn":
      return Array.isArray(value)
        ? !(value as string[]).map((v) => v.toLowerCase()).includes(cvStr)
        : true;
    case "isEmpty":
      return cv === null || cv === undefined || cv === "";
    case "isNotEmpty":
      return cv !== null && cv !== undefined && cv !== "";
    default:
      return true;
  }
}

/**
 * Estimate the height of a row for the virtualizer. Density-aware via CSS
 * variables would require reading runtime layout, which jsdom doesn't expose
 * — so we hard-code 44px (matches the comfortable density's `--aio-row-height`
 * default). When density is compact, rows compress visually; the virtualizer
 * just over-provisions slightly which is harmless.
 */
const DEFAULT_ROW_HEIGHT = 44;

/**
 * Async-data state machine. The component holds one of these shapes when in
 * server-side mode; client-side mode never observes anything beyond `idle`.
 */
type AsyncState<TRow> =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; rows: TRow[]; total: number }
  | { status: "error"; error: Error };

/**
 * The single public composer for the AIO table engine.
 *
 * Layout:
 *
 *   ┌─────────────────────────────────────────────────────────┐
 *   │ [ filter bar ] [ saved views ] [ columns ] [ export ] │ ← toolbar
 *   │                                          [ primary  ] │
 *   ├─────────────────────────────────────────────────────────┤
 *   │ [ sticky header ]                                       │
 *   │ [ virtualized rows ]                                    │
 *   ├─────────────────────────────────────────────────────────┤
 *   │ [ pagination ]                                          │
 *   ├─────────────────────────────────────────────────────────┤
 *   │ [ bulk action bar (visible when ≥1 selected) ]          │
 *   └─────────────────────────────────────────────────────────┘
 *
 * Modes:
 * - **Client-side** (`data` provided): TanStack handles sort / filter /
 *   pagination locally.
 * - **Server-side** (`dataSource` provided): the loader is invoked whenever
 *   sorting / filters / pagination changes, and the table renders whatever
 *   page the loader returned.
 *
 * Composition:
 * - `useTableState` owns persisted slices (sort, filters, sizing, ...).
 * - `useReactTable` is wired to that state via controlled `onChange` setters.
 * - `useVirtualRows` engages above {@link VIRTUALIZE_THRESHOLD} rows.
 * - `useSavedViews` keys off `persistKey` for the localStorage bucket.
 * - `useKeyboardNav` provides arrow / j / k / Enter / Space / Cmd+A.
 */
export function DataTable<TRow>(
  props: DataTableProps<TRow>,
): React.JSX.Element {
  const {
    columns,
    data,
    dataSource,
    rowId,
    persistKey,
    primaryAction,
    bulkActions,
    emptyState,
    onRowActivate,
    onExport,
    locale = "en",
    formatters = defaultFormatters,
    className,
    "data-testid": dataTestId = "data-table",
  } = props;

  // SSR-safety gate. `useTableState` and `useSavedViews` both read
  // `localStorage` in their `useState` initializers, so on the server they
  // resolve to defaults while on the client they resolve to whatever the
  // user has persisted (column order, widths, sorts, filters, hidden
  // columns). Rendering the table from those divergent states would emit
  // a different DOM tree on the client than the server sent — Next.js
  // logs a hydration error and React aborts the patch. Solution: render a
  // stable `LoadingTable` placeholder until after first mount, then
  // unblock the real tree once we know we're on the client. The hooks
  // themselves still run during SSR (they have to — they're called
  // unconditionally), they just don't reach a render path that exposes
  // the divergent state. This costs one extra render after hydration but
  // eliminates the entire class of localStorage-driven SSR mismatches.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Pre-flight validation. Both modes is a contract violation; surface it
  // exactly once via a console.warn. The component itself proceeds with
  // dataSource priority — silently dropping `data` is preferable to throwing
  // at runtime in production.
  React.useEffect(() => {
    if (data !== undefined && dataSource !== undefined) {
      console.warn(
        "[DataTable] Both `data` and `dataSource` are set; `dataSource` wins. Pass exactly one.",
      );
    }
    if (data === undefined && dataSource === undefined) {
      console.warn(
        "[DataTable] Neither `data` nor `dataSource` is set — rendering as empty.",
      );
    }
  }, [data, dataSource]);

  const isServerMode = dataSource !== undefined;

  // Persisted state container. The hook owns the slices that need to survive
  // remount; `set` exposes per-slice setters wired into TanStack's onChange
  // callbacks below.
  const tableStateHook = useTableState(undefined, persistKey);
  const { state, set, reset } = tableStateHook;

  // Server-side data state machine. Idle in client-side mode.
  const [serverData, setServerData] = React.useState<AsyncState<TRow>>({
    status: "idle",
  });

  // Build TanStack columns. Memoised on `columns` identity so referentially
  // stable callers don't pay the cost on every render.
  const tanStackColumns = React.useMemo<TanStackColumnDef<TRow>[]>(
    () => columns.map((c) => toTanStackColumn(c)),
    [columns],
  );

  // Resolve the row dataset. In server mode, fall through to the loader's
  // last-good payload; in client mode, just use the provided array.
  const resolvedData = React.useMemo<TRow[]>(() => {
    if (isServerMode) {
      return serverData.status === "success" ? serverData.rows : [];
    }
    return data ?? [];
  }, [isServerMode, serverData, data]);

  // Server-side total used to derive page count when TanStack can't compute
  // it locally. In client mode, TanStack uses the data length itself.
  const serverTotal = isServerMode && serverData.status === "success"
    ? serverData.total
    : 0;

  // Tan-Stack table instance. Controlled state slices are wired through the
  // `onChange` callbacks so the persisted state hook becomes the source of
  // truth.
  const table = useReactTable<TRow>({
    data: resolvedData,
    columns: tanStackColumns,
    state: {
      columnVisibility: state.columnVisibility,
      columnSizing: state.columnSizes,
      columnOrder: state.columnOrder,
      sorting: state.sorting,
      columnFilters: state.columnFilters,
      pagination: state.pagination,
      rowSelection: state.rowSelection,
      globalFilter: state.globalFilter,
    },
    enableRowSelection: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    getRowId: (row) => rowId(row),
    onColumnVisibilityChange: (updater) => {
      const next: VisibilityState =
        typeof updater === "function" ? updater(state.columnVisibility) : updater;
      set.columnVisibility(next);
    },
    onColumnSizingChange: (updater) => {
      const next: ColumnSizingState =
        typeof updater === "function" ? updater(state.columnSizes) : updater;
      set.columnSizes(next);
    },
    onColumnOrderChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(state.columnOrder) : updater;
      set.columnOrder(next);
    },
    onSortingChange: (updater) => {
      const next: SortingState =
        typeof updater === "function" ? updater(state.sorting) : updater;
      set.sorting(next);
      // Reset to page 1 on sort change so the user isn't stranded on a page
      // that no longer makes sense after re-ordering.
      set.pagination({ ...state.pagination, pageIndex: 0 });
    },
    onColumnFiltersChange: (updater) => {
      const next: ColumnFiltersState =
        typeof updater === "function" ? updater(state.columnFilters) : updater;
      set.columnFilters(next);
      set.pagination({ ...state.pagination, pageIndex: 0 });
    },
    onPaginationChange: (updater) => {
      const next: PaginationState =
        typeof updater === "function" ? updater(state.pagination) : updater;
      set.pagination(next);
    },
    onRowSelectionChange: (updater) => {
      const next: RowSelectionState =
        typeof updater === "function" ? updater(state.rowSelection) : updater;
      set.rowSelection(next);
    },
    onGlobalFilterChange: (updater) => {
      const next: string =
        typeof updater === "function" ? updater(state.globalFilter) : updater;
      set.globalFilter(next);
    },
    // Client-side row models. In server mode we still use the core / sorted /
    // filtered models for selection bookkeeping but rely on the loader's
    // pagination — the data passed in is already the page slice.
    getCoreRowModel: getCoreRowModel(),
    ...(isServerMode
      ? {
          // Manual everything: server returns the page directly.
          manualPagination: true,
          manualSorting: true,
          manualFiltering: true,
          // Page count computed from server total; fall back to 1 on idle.
          pageCount: Math.max(
            1,
            Math.ceil(serverTotal / Math.max(1, state.pagination.pageSize)),
          ),
        }
      : {
          getSortedRowModel: getSortedRowModel(),
          getFilteredRowModel: getFilteredRowModel(),
          getPaginationRowModel: getPaginationRowModel(),
          // Wire structured filter dispatch — TanStack's default just
          // string-includes the raw value, which is wrong for our shape.
          filterFns: {},
          defaultColumn: {
            filterFn: (row, columnId, filterValue) =>
              evaluateFilter(row.getValue(columnId), filterValue),
          },
        }),
  });

  // Saved views — the localStorage bucket is keyed off `persistKey` so two
  // tables on the same page never collide. We use `persistKey ?? '__none'`
  // for the storage key when not provided; the menu still works in-memory.
  const savedViews = useSavedViews({
    tableKey: persistKey ?? "__inline",
    currentState: state,
    onApply: (next) => {
      // `apply` rewrites every persistable slice but leaves selection /
      // global filter alone so a user applying a view doesn't lose their
      // current selection or quick-search.
      set.columnVisibility(next.columnVisibility);
      set.columnSizes(next.columnSizes);
      set.columnOrder(next.columnOrder);
      set.sorting(next.sorting);
      set.columnFilters(next.columnFilters);
      set.pagination(next.pagination);
    },
  });

  // Active filters surfaced to the FilterBar. Mirrors `state.columnFilters`
  // through the structured ↔ ActiveFilter encoder so the bar stays the source
  // of truth for chips while TanStack remains the source of truth for
  // dispatch.
  const activeFilters = React.useMemo<ActiveFilter[]>(
    () => columnFiltersToActiveFilters(state.columnFilters),
    [state.columnFilters],
  );

  const handleFiltersChange = React.useCallback(
    (next: ActiveFilter[]) => {
      set.columnFilters(activeFiltersToColumnFilters(next));
      set.pagination({ ...state.pagination, pageIndex: 0 });
    },
    // We deliberately depend only on `set` and `state.pagination` — the
    // setter handles both slices in lock-step.
    [set, state.pagination],
  );

  // Outer container ref for keyboard nav. Scope the listener to the table so
  // multiple tables on a page don't fight.
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  // Visible row model (post-filter, pre-pagination) drives keyboard nav row
  // count; the visual rendering uses the paginated row model.
  const filteredRowCount = isServerMode
    ? serverTotal
    : table.getFilteredRowModel().rows.length;

  useKeyboardNav({
    containerRef,
    rowCount: filteredRowCount,
    onActivate: (idx) => {
      const row = table.getRowModel().rows[idx];
      if (row && onRowActivate) onRowActivate(row.original);
    },
    onToggleSelect: (idx) => {
      const row = table.getRowModel().rows[idx];
      if (row) row.toggleSelected();
    },
    onSelectAll: () => table.toggleAllRowsSelected(true),
  });

  // Hold a fresh ref to the current state so the loader effect can read it
  // without forcing a re-fetch on every minor slice change (selection,
  // sizing). We re-trigger the effect only when the slices the loader cares
  // about change.
  const stateRef = React.useRef(state);
  stateRef.current = state;

  // Server-side loader. Re-fires whenever sort / filters / pagination /
  // global filter change. We keep an AbortController so a new request
  // supersedes any in-flight one cleanly.
  React.useEffect(() => {
    if (!isServerMode || !dataSource) return;

    const controller = new AbortController();
    let cancelled = false;
    setServerData({ status: "loading" });

    dataSource(stateRef.current)
      .then((result) => {
        if (cancelled || controller.signal.aborted) return;
        setServerData({
          status: "success",
          rows: result.rows,
          total: result.total,
        });
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return;
        setServerData({
          status: "error",
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // We deliberately depend only on the slices the server cares about. The
    // `dataSource` reference is included so swapping the loader re-fetches.
  }, [
    isServerMode,
    dataSource,
    state.sorting,
    state.columnFilters,
    state.pagination,
    state.globalFilter,
  ]);

  // Virtualization gates only on the rendered (paginated) row count. We feed
  // it the page-level row model length so a 10,000-row page-2 doesn't blow
  // up the DOM.
  const pageRows = table.getRowModel().rows;
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const virtual = useVirtualRows({
    count: pageRows.length,
    estimateSize: () => DEFAULT_ROW_HEIGHT,
    getScrollElement: () => scrollRef.current,
    overscan: 8,
  });
  const isVirtualized = virtual.virtualizer !== null;

  // Selected row ids in display order. Computed once per render so each
  // bulk-action callback gets a fresh snapshot.
  const selectedRowIds = React.useMemo(() => {
    const sel = state.rowSelection;
    return Object.keys(sel).filter((k) => sel[k]);
  }, [state.rowSelection]);

  // Export wiring. The menu emits a format id; the caller is responsible for
  // hooking up real writers (toCsv / toXlsx / toPdf). When no `onExport` prop
  // is provided we surface a dev-only console.warn so callers know the menu
  // is wired but no writer is registered yet — production callers always
  // pass `onExport`.
  //
  // The snapshot we hand back is the post-sort, post-filter, pre-pagination
  // row set. That matches the common user intent ("export what I'm looking
  // at, but everything that matched, not just this page"). Visible columns
  // are filtered to ones not hidden via the column-visibility menu.
  const handleExport = React.useCallback(
    (format: ExportFormat) => {
      if (!onExport) {
        console.warn(
          `[DataTable] Export to ${format} requested but no writer is wired. Pass the \`onExport\` prop or override via app-level toolbar customization.`,
        );
        return undefined;
      }
      // The pre-pagination row model exposes the filtered + sorted rows.
      // In server mode this is the current page only — the loader is the
      // source of truth and the engine doesn't keep prior pages in memory.
      const snapshotRows = (
        isServerMode
          ? table.getRowModel().rows
          : table.getSortedRowModel().rows.filter((r) => {
              // Re-apply column-filters explicitly because getSortedRowModel
              // doesn't run filters; getFilteredRowModel does, so use that.
              return r;
            })
      ).map((r) => r.original);
      const filteredRows = isServerMode
        ? snapshotRows
        : table.getFilteredRowModel().rows.map((r) => r.original);
      // Visible columns in toolbar order. We map back to the platform
      // ColumnDef so the writer picks up enumLabels / relationLabel / format
      // overrides — TanStack's column shape doesn't carry those.
      const visibleKeys = new Set(
        table.getVisibleLeafColumns().map((c) => c.id),
      );
      const snapshotCols = columns.filter((c) => visibleKeys.has(c.key));
      return onExport(format, { rows: filteredRows, columns: snapshotCols });
    },
    [onExport, isServerMode, table, columns],
  );

  // Compute the visible-column count once for the various colSpan-based
  // states (empty / loading / error). +1 for the selection cell.
  const totalColumnCount = table.getVisibleLeafColumns().length + 1;

  // Body content branches over async state in server mode and over the
  // resolved row count otherwise. In client mode the loading branch never
  // fires; we keep the structure uniform so the markup stays predictable
  // for tests.
  const bodyContent = (() => {
    if (isServerMode && serverData.status === "loading") {
      return <LoadingTable columnCount={totalColumnCount} />;
    }
    if (isServerMode && serverData.status === "error") {
      return (
        <ErrorTable
          colSpan={totalColumnCount}
          description={serverData.error.message}
          onRetry={() => {
            // Re-trigger the loader by nudging pagination to itself.
            set.pagination({ ...state.pagination });
          }}
        />
      );
    }

    if (pageRows.length === 0) {
      // Custom empty-state slot wins; otherwise default to EmptyTable.
      if (emptyState) {
        return (
          <tr data-testid={`${dataTestId}-empty-row`}>
            <td colSpan={totalColumnCount} className="p-0">
              {emptyState}
            </td>
          </tr>
        );
      }
      return <EmptyTable colSpan={totalColumnCount} />;
    }

    if (isVirtualized && virtual.virtualizer) {
      // Render an absolutely-positioned slice of rows so the virtualizer
      // keeps the DOM small. The wrapper `<tr>` carries the totalSize as a
      // single tall row's height — TanStack docs cover this pattern.
      const items = virtual.virtualRows;
      const rowsToRender = items.map((vi) => {
        const row = pageRows[vi.index];
        if (!row) return null;
        const platformCol = columns;
        return (
          <DataRow
            key={row.id}
            row={row}
            table={table}
            columns={platformCol}
            locale={locale}
            formatters={formatters}
            {...(onRowActivate ? { onActivate: onRowActivate } : {})}
          />
        );
      });
      return rowsToRender;
    }

    return pageRows.map((row) => (
      <DataRow
        key={row.id}
        row={row}
        table={table}
        columns={columns}
        locale={locale}
        formatters={formatters}
        {...(onRowActivate ? { onActivate: onRowActivate } : {})}
      />
    ));
  })();

  if (!mounted) {
    // Pre-mount: render a stable placeholder that matches between server
    // and client. We deliberately keep the same outer wrapper element + cn
    // / data-testid as the real table so layout shift is minimal when the
    // skeleton swaps out for the real tree.
    return (
      <div
        ref={containerRef}
        tabIndex={-1}
        className={cn("flex flex-col gap-2", className)}
        data-testid={dataTestId}
      >
        <LoadingTable rowCount={8} />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className={cn("flex flex-col gap-2", className)}
      data-testid={dataTestId}
    >
      {/* Toolbar */}
      <div
        className="flex flex-wrap items-center justify-between gap-2"
        data-testid={`${dataTestId}-toolbar`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <SavedViewsMenu views={savedViews} />
          <ColumnVisibility table={table} />
          <ExportMenu onSelect={handleExport} />
          {persistKey ? (
            <button
              type="button"
              onClick={reset}
              className={cn(
                "rounded-md border border-border bg-bg px-3 py-1.5 text-sm text-fg-muted",
                "transition-colors duration-fast ease-standard hover:bg-bg-subtle",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
              data-testid={`${dataTestId}-reset`}
            >
              Reset
            </button>
          ) : null}
        </div>
        {primaryAction ? (
          <div data-testid={`${dataTestId}-primary-action`}>{primaryAction}</div>
        ) : null}
      </div>

      {/* Filter bar */}
      <FilterBar
        columns={columns}
        filters={activeFilters}
        onChange={handleFiltersChange}
      />

      {/* Selection-anchor scope wraps the table so shift-click works across
          virtualized boundaries. */}
      <SelectionAnchorProvider>
        <div
          ref={scrollRef}
          // `overflow-x-auto` carries the horizontal scroll; the inset
          // box-shadow on the right edge fades a soft "more-content" hint
          // without a gradient. `scrollbar-gutter: stable` reserves the
          // scrollbar track so the table doesn't jump width when content
          // toggles between scrollable and not.
          className="relative max-h-[calc(100vh-20rem)] overflow-auto rounded-md border border-border bg-bg [scrollbar-gutter:stable] shadow-[inset_-8px_0_8px_-8px_hsl(var(--border-strong)/0.6)]"
          data-testid={`${dataTestId}-scroll`}
        >
          <table
            className="w-full border-collapse text-sm"
            style={{
              // Lock the layout so the virtualizer's totalSize is honoured.
              tableLayout: "fixed",
              ...(isVirtualized
                ? { height: virtual.totalSize + DEFAULT_ROW_HEIGHT }
                : {}),
            }}
          >
            <TableHeader
              table={table}
              renderColumnAction={(header) => (
                <ColumnMenu column={header.column} table={table} />
              )}
            />
            <tbody>{bodyContent}</tbody>
          </table>
        </div>
      </SelectionAnchorProvider>

      {/* Pagination */}
      <Pagination
        table={table}
        formatTotal={(count) => `${count} ${count === 1 ? "row" : "rows"}`}
      />

      {/* Bulk action bar — only renders when ≥1 row is selected. The bar is
          itself responsible for the count check; we render it unconditionally
          and let it return null when zero. */}
      <BulkActionBar
        table={table}
        actions={
          bulkActions && bulkActions.length > 0 ? (
            <div className="flex items-center gap-2">
              {bulkActions.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  onClick={() => action.onRun(Array.from(selectedRowIds))}
                  className={cn(
                    "rounded-md border border-border bg-bg px-3 py-1.5 text-sm text-fg",
                    "transition-colors duration-fast ease-standard hover:bg-bg-subtle",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  )}
                  data-testid={`${dataTestId}-bulk-${action.id}`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          ) : null
        }
      />

    </div>
  );
}
